import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    CreatePackagingMaterialRequest,
    CreatePackagingStockInRequest,
    ListPackagingInventoryParams,
    ListPackagingLotsParams,
    ListPackagingMaterialsParams,
    ListPackagingStockInParams,
    ListPackagingStockOutParams,
    ListPackagingTransactionsParams,
    PackagingMovementParams,
    ReplaceBOMRequest,
    UpdatePackagingMaterialRequest,
} from '@/lib/api/packaging-api'
import { packagingApi } from '@/lib/api/packaging-api'
import { formatApiError } from '@/lib/format-api-error'
import { m } from '@/lib/paraglide/messages'

export const packagingKeys = {
    all: ['packaging'] as const,
    materials: (params?: ListPackagingMaterialsParams) =>
        [...packagingKeys.all, 'materials', params ?? {}] as const,
    materialsInfinite: (params?: ListPackagingMaterialsParams) =>
        [...packagingKeys.all, 'materials-infinite', params ?? {}] as const,
    material: (id: string) => [...packagingKeys.all, 'material', id] as const,
    bom: (productId: string) =>
        [...packagingKeys.all, 'bom', productId] as const,
    bomCounts: (productIds: string[]) =>
        [
            ...packagingKeys.all,
            'bom-counts',
            productIds.slice().sort().join(','),
        ] as const,
    stockIn: (params?: ListPackagingStockInParams) =>
        [...packagingKeys.all, 'stock-in', params ?? {}] as const,
    stockInDetail: (id: string) =>
        [...packagingKeys.all, 'stock-in', 'detail', id] as const,
    stockOut: (params?: ListPackagingStockOutParams) =>
        [...packagingKeys.all, 'stock-out', params ?? {}] as const,
    stockOutDetail: (id: string) =>
        [...packagingKeys.all, 'stock-out', 'detail', id] as const,
    lots: (params?: ListPackagingLotsParams) =>
        [...packagingKeys.all, 'lots', params ?? {}] as const,
    lot: (id: string) => [...packagingKeys.all, 'lots', 'detail', id] as const,
    inventory: (params?: ListPackagingInventoryParams) =>
        [...packagingKeys.all, 'inventory', params ?? {}] as const,
    transactions: (params?: ListPackagingTransactionsParams) =>
        [...packagingKeys.all, 'transactions', params ?? {}] as const,
    movement: (params?: PackagingMovementParams) =>
        [...packagingKeys.all, 'movement', params ?? {}] as const,
    movementBreakdown: (
        params?: PackagingMovementParams & { top_n?: number },
    ) => [...packagingKeys.all, 'movement-breakdown', params ?? {}] as const,
}

export function usePackagingMaterials(params?: ListPackagingMaterialsParams) {
    return useQuery({
        queryKey: packagingKeys.materials(params),
        queryFn: async () => {
            const res = await packagingApi.listMaterials(params)
            return { items: res.data ?? [], meta: res.meta }
        },
    })
}

const MATERIALS_PAGE_SIZE = 30

/**
 * Danh sách vật tư dạng cuộn-tải-thêm cho ô chọn vật tư.
 *
 * `enabled` để component nào có nhiều biến thể picker không bắn cả hai query
 * cùng lúc. Trang tiếp dựa vào `meta.total_pages` — nếu suy từ độ dài items thì
 * trang cuối trả rỗng nhưng thiếu meta sẽ thành vòng lặp vô hạn.
 */
export function useInfinitePackagingMaterials(
    params?: Omit<ListPackagingMaterialsParams, 'page' | 'per_page'>,
    enabled = true,
) {
    return useInfiniteQuery({
        queryKey: packagingKeys.materialsInfinite(params),
        enabled,
        initialPageParam: 1,
        queryFn: async ({ pageParam }) => {
            const res = await packagingApi.listMaterials({
                ...params,
                page: pageParam,
                per_page: MATERIALS_PAGE_SIZE,
            })
            return { items: res.data ?? [], meta: res.meta }
        },
        getNextPageParam: (lastPage, allPages) => {
            const totalPages = lastPage.meta?.total_pages ?? 0
            const next = allPages.length + 1
            return next <= totalPages ? next : undefined
        },
    })
}

/**
 * Lấy một vật tư theo id — dùng làm phương án dự phòng cho ô chọn vật tư.
 *
 * Picker chỉ có trong tay những trang đã tải; giá trị đã chọn sẵn (VD dòng BOM
 * đang sửa) có thể nằm ở trang chưa tải, khi đó ô sẽ hiện placeholder thay vì
 * tên vật tư. Query này lấp đúng chỗ đó, và chỉ chạy khi thật sự thiếu.
 */
export function usePackagingMaterial(id?: string, enabled = true) {
    return useQuery({
        queryKey: packagingKeys.material(id ?? ''),
        enabled: Boolean(id) && enabled,
        queryFn: async () => {
            const res = await packagingApi.getMaterial(id as string)
            return res.data ?? null
        },
    })
}

export function useCreatePackagingMaterial() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: CreatePackagingMaterialRequest) =>
            packagingApi.createMaterial(data),
        onSuccess: () => {
            toast.success(m.packaging_material_save_success())
            void qc.invalidateQueries({ queryKey: packagingKeys.all })
        },
        onError: (err) => {
            const payload = formatApiError(
                err,
                m.packaging_material_save_error(),
            )
            toast.error(payload.title, { description: payload.description })
        },
    })
}

export function useUpdatePackagingMaterial() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            id,
            data,
        }: {
            id: string
            data: UpdatePackagingMaterialRequest
        }) => packagingApi.updateMaterial(id, data),
        onSuccess: () => {
            toast.success(m.packaging_material_save_success())
            void qc.invalidateQueries({ queryKey: packagingKeys.all })
        },
        onError: (err) => {
            const payload = formatApiError(
                err,
                m.packaging_material_save_error(),
            )
            toast.error(payload.title, { description: payload.description })
        },
    })
}

export function useDeletePackagingMaterial() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => packagingApi.deleteMaterial(id),
        onSuccess: () => {
            toast.success(m.packaging_material_delete_success())
            void qc.invalidateQueries({ queryKey: packagingKeys.all })
        },
        onError: (err) => {
            const payload = formatApiError(
                err,
                m.packaging_material_delete_error(),
            )
            toast.error(payload.title, { description: payload.description })
        },
    })
}

export function useProductBOM(productId: string, enabled = true) {
    return useQuery({
        queryKey: packagingKeys.bom(productId),
        queryFn: async () => {
            const res = await packagingApi.getBOM(productId)
            return res.data ?? []
        },
        enabled: enabled && Boolean(productId),
    })
}

export function useReplaceBOM() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            productId,
            data,
        }: {
            productId: string
            data: ReplaceBOMRequest
        }) => packagingApi.replaceBOM(productId, data),
        onSuccess: (_res, vars) => {
            toast.success(m.packaging_bom_save_success())
            void qc.invalidateQueries({
                queryKey: packagingKeys.bom(vars.productId),
            })
            void qc.invalidateQueries({
                queryKey: [...packagingKeys.all, 'bom-counts'],
            })
        },
        onError: (err) => {
            const payload = formatApiError(err, m.packaging_bom_save_error())
            toast.error(payload.title, { description: payload.description })
        },
    })
}

// --- Phase 2: stock-in / inventory / transactions ---

export function usePackagingStockInList(params?: ListPackagingStockInParams) {
    return useQuery({
        queryKey: packagingKeys.stockIn(params),
        queryFn: async () => {
            const res = await packagingApi.listStockIn(params)
            return { items: res.data ?? [], meta: res.meta }
        },
    })
}

export function usePackagingStockInDetail(id: string, enabled = true) {
    return useQuery({
        queryKey: packagingKeys.stockInDetail(id),
        queryFn: async () => {
            const res = await packagingApi.getStockIn(id)
            return res.data
        },
        enabled: enabled && Boolean(id),
    })
}

export function usePackagingStockOutList(params?: ListPackagingStockOutParams) {
    return useQuery({
        queryKey: packagingKeys.stockOut(params),
        queryFn: async () => {
            const res = await packagingApi.listStockOut(params)
            return { items: res.data ?? [], meta: res.meta }
        },
    })
}

export function usePackagingStockOutDetail(id: string, enabled = true) {
    return useQuery({
        queryKey: packagingKeys.stockOutDetail(id),
        queryFn: async () => {
            const res = await packagingApi.getStockOut(id)
            return res.data
        },
        enabled: enabled && Boolean(id),
    })
}

export function useCreatePackagingStockIn() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: CreatePackagingStockInRequest) =>
            packagingApi.createStockIn(data),
        onSuccess: () => {
            toast.success(m.packaging_stock_in_success())
            // Stock-in updates inventory + transactions too — invalidate hết
            // sub-tree packaging để mọi page tự refresh.
            void qc.invalidateQueries({ queryKey: packagingKeys.all })
        },
        onError: (err) => {
            const payload = formatApiError(err, m.packaging_stock_in_error())
            toast.error(payload.title, { description: payload.description })
        },
    })
}

export function usePackagingInventory(params?: ListPackagingInventoryParams) {
    return useQuery({
        queryKey: packagingKeys.inventory(params),
        queryFn: async () => {
            const res = await packagingApi.listInventory(params)
            return { items: res.data ?? [], meta: res.meta }
        },
    })
}

export function usePackagingTransactions(
    params?: ListPackagingTransactionsParams,
) {
    return useQuery({
        queryKey: packagingKeys.transactions(params),
        queryFn: async () => {
            const res = await packagingApi.listTransactions(params)
            return { items: res.data ?? [], meta: res.meta }
        },
    })
}

export function usePackagingLots(params?: ListPackagingLotsParams) {
    return useQuery({
        queryKey: packagingKeys.lots(params),
        queryFn: async () => {
            const res = await packagingApi.listLots(params)
            return { items: res.data ?? [], meta: res.meta }
        },
    })
}

export function usePackagingLot(id: string, enabled = true) {
    return useQuery({
        queryKey: packagingKeys.lot(id),
        queryFn: async () => {
            const res = await packagingApi.getLot(id)
            return res.data
        },
        enabled: enabled && Boolean(id),
    })
}

// 5-min stale matches warehouse-sales dashboard.
const MOVEMENT_STALE_MS = 5 * 60 * 1000

export function usePackagingMovement(params: PackagingMovementParams) {
    return useQuery({
        queryKey: packagingKeys.movement(params),
        queryFn: async () => {
            const res = await packagingApi.movement(params)
            return res.data
        },
        staleTime: MOVEMENT_STALE_MS,
    })
}

export function usePackagingMovementBreakdown(
    params: PackagingMovementParams & { top_n?: number },
) {
    return useQuery({
        queryKey: packagingKeys.movementBreakdown(params),
        queryFn: async () => {
            const res = await packagingApi.movementBreakdown(params)
            return res.data
        },
        staleTime: MOVEMENT_STALE_MS,
    })
}

// Phase 4 — fetch suggested packaging từ BOM cho các product items đã chọn.
// Dùng làm autofill UI lúc tạo đơn. Mutation-style vì items thay đổi liên tục.
export function useCheckPackagingAvailability() {
    return useMutation({
        mutationFn: (items: { product_id: string; quantity: number }[]) =>
            packagingApi.checkAvailability(items),
    })
}

// Vật tư đóng gói SP có giá bán → dòng khoá (charge_only) tính tiền vào đơn.
export function useCheckPackagingSurcharge() {
    return useMutation({
        mutationFn: (items: { product_id: string; quantity: number }[]) =>
            packagingApi.checkSurcharge(items),
    })
}

export function useBOMCounts(productIds: string[]) {
    return useQuery({
        queryKey: packagingKeys.bomCounts(productIds),
        queryFn: async () => {
            const res = await packagingApi.bomCounts(productIds)
            return res.data ?? {}
        },
        enabled: productIds.length > 0,
    })
}
